import React, { useCallback, useEffect, useState } from 'react';
// [Web UX 3. csomag (D), 2026-10-04] A kérés-hibák szövege a közös `apiErrorMessage`-en át megy (a szerver magyar
// indoklása / hálózati hibánál magyar mondat / különben „<tartalék> (HTTP n)") — a korábbi FIX
// szövegű ágak eldobták a szerver indoklását. Őr: `utils/__tests__/rawErrorMessages.guard.test.ts`.
import { apiErrorMessage } from '../utils/apiErrors';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItemButton,
    ListItemText,
    Typography,
    Box,
    IconButton,
    CircularProgress,
    Alert,
    Divider,
    Tooltip,
    Tabs,
    Tab,
    Chip,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import EventIcon from '@mui/icons-material/Event';
import ReplyIcon from '@mui/icons-material/Reply';
import CreateIcon from '@mui/icons-material/Create';
import { useResponsive } from '../hooks/useResponsive';
import {
    messageService,
    isDeletedMessageError,
    MessageListItem,
    MessageDetail,
    SentMessageListItem,
    SentMessageDetail,
} from '../services/messageService';
import ComposeMessageDialog, { ComposePrefill } from './ComposeMessageDialog';
import { useFeedback } from '../contexts/FeedbackContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { formatServerDateTime } from '../utils/dateUtils';

interface MessageInboxProps {
    open: boolean;
    onClose: () => void;
    /** A felső sáv jelvényének frissítéséhez, ha a postafiókban változik az olvasottság. */
    onUnreadChange?: () => void;
}

type InboxView = 'inbox' | 'sent';

// [Web UX 1. csomag (4.3), 2026-10-03] az üzenetek `sentDate`/`readDate`-je „Z”-vel jön (a szerver `SpecifyKind(…, Utc)`,
// `MessagesController.cs:179`) — ez eddig is helyes volt; a közös segéd a „Z”-s szöveget változatlanul
// értelmezi, és ugyanazt az alakot adja (`2026. 10. 02. 15:15`), így a kliensen EGY megoldás él.
function formatDate(iso: string): string {
    return formatServerDateTime(iso);
}

/**
 * Postafiók a webes felületen.
 *
 * Eddig az üzeneteket CSAK a mobilappban lehetett visszaolvasni: aki weben dolgozott,
 * annak a saját email-fiókjában kellett keresnie. Ez a párbeszédablak ugyanazokat a
 * végpontokat használja, mint a mobil, így a két felület ugyanazt mutatja — a mobilon
 * eldobott üzenet itt sem bukkan fel újra.
 *
 * 2026-09-11 óta írni is lehet innen (klub-kérés): „Új üzenet" a saját címzettlistából
 * (a szabály a szerveren él), „Válasz" a kapott üzenetre, és az „Elküldött" fül a saját
 * küldeményekkel — törléssel, ami a címzetteknél nem vesz el semmit.
 */
const MessageInbox: React.FC<MessageInboxProps> = ({ open, onClose, onUnreadChange }) => {
    // Telefonon teljes képernyő, ahogy a többi párbeszédablaknál is: egy postafiók
    // listát olvashatatlanná tesz a kis, margós ablak.
    const { isMobile } = useResponsive();
    const { showSuccess } = useFeedback();
    const confirm = useConfirm();
    const [view, setView] = useState<InboxView>('inbox');
    const [messages, setMessages] = useState<MessageListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [selected, setSelected] = useState<MessageDetail | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState('');
    /** Külön a hálózati hibától: ezt a felhasználó maga törölte (410 Gone). */
    const [detailDeleted, setDetailDeleted] = useState(false);

    const [sent, setSent] = useState<SentMessageListItem[]>([]);
    const [sentLoading, setSentLoading] = useState(false);
    const [sentError, setSentError] = useState('');
    const [selectedSent, setSelectedSent] = useState<SentMessageDetail | null>(null);

    const [composeOpen, setComposeOpen] = useState(false);
    const [composePrefill, setComposePrefill] = useState<ComposePrefill | undefined>(undefined);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await messageService.getMyMessages();
            setMessages(data);
        } catch (err) {
            // Üres lista helyett hibaüzenet: különben a felhasználó azt hinné, nincs üzenete.
            setError(apiErrorMessage(err, 'Nem sikerült betölteni az üzeneteket.'));
        } finally {
            setLoading(false);
        }
    }, []);

    const loadSent = useCallback(async () => {
        setSentLoading(true);
        setSentError('');
        try {
            setSent(await messageService.getSentMessages());
        } catch (err) {
            setSentError(apiErrorMessage(err, 'Nem sikerült betölteni az elküldött üzeneteket.'));
        } finally {
            setSentLoading(false);
        }
    }, []);

    useEffect(() => {
        if (open) {
            setView('inbox');
            setSelected(null);
            setSelectedSent(null);
            setDetailError('');
            setDetailDeleted(false);
            load();
        }
    }, [open, load]);

    // [Web UX 3. csomag (A), 1. javító kör — validátor F3, 2026-10-04] A postafiókot a szülő a beágyazott
    // írás FÖLÖTT is bezárhatja (a böngésző Vissza gombja → `Dashboard.closeDashboardDialogs`). Ilyenkor
    // az írás is zárul: különben a `composeOpen` igaz maradna, a következő megnyitáskor magától
    // felugrana az (üres) írás-ablak, és a zárás közben az írás mentetlen-bejelentése is kitartana
    // (`ComposeMessageDialog` `isDirty = open && …`).
    useEffect(() => {
        if (!open) setComposeOpen(false);
    }, [open]);

    useEffect(() => {
        if (open && view === 'sent') loadSent();
    }, [open, view, loadSent]);

    const openMessage = async (item: MessageListItem) => {
        setDetailLoading(true);
        setDetailError('');
        setDetailDeleted(false);
        try {
            const detail = await messageService.getById(item.messageId);
            setSelected(detail);
            // A megnyitás a szerveren olvasottá tette — a listát és a jelvényt igazítjuk.
            setMessages((prev) =>
                prev.map((m) => (m.messageId === item.messageId ? { ...m, isRead: true } : m)),
            );
            onUnreadChange?.();
        } catch (err) {
            if (isDeletedMessageError(err)) {
                setDetailDeleted(true);
            } else {
                setDetailError(apiErrorMessage(err, 'Nem sikerült betölteni az üzenetet.'));
            }
            setSelected(null);
        } finally {
            setDetailLoading(false);
        }
    };

    const openSent = async (item: SentMessageListItem) => {
        setDetailLoading(true);
        setDetailError('');
        try {
            setSelectedSent(await messageService.getSentMessage(item.messageId));
        } catch (err) {
            setDetailError(apiErrorMessage(err, 'Nem sikerült betölteni az üzenetet.'));
            setSelectedSent(null);
        } finally {
            setDetailLoading(false);
        }
    };

    const handleDelete = async (messageId: string, subject: string) => {
        // Megerősítés nélkül egy félrekattintás visszavonhatatlanul eltüntetné az üzenetet
        // a felhasználó szemszögéből (visszaállításra ma nincs felület).
        if (!(await confirm({
            title: 'Üzenet törlése',
            message: `Biztosan törlöd ezt az üzenetet?\n\n„${subject}"`,
            confirmLabel: 'Törlés',
            destructive: true,
        }))) return;
        try {
            await messageService.remove(messageId);
            setMessages((prev) => prev.filter((m) => m.messageId !== messageId));
            if (selected?.messageId === messageId) setSelected(null);
            onUnreadChange?.();
            showSuccess('Az üzenet törölve.');
        } catch (err) {
            setError(apiErrorMessage(err, 'Az üzenetet nem sikerült törölni. Próbáld újra.'));
        }
    };

    const handleDeleteSent = async (messageId: string, subject: string) => {
        if (!(await confirm({
            title: 'Üzenet eltávolítása',
            message: `Eltávolítod az elküldöttek közül?\n\n„${subject}"\n\nA címzettnél az üzenet megmarad.`,
            confirmLabel: 'Eltávolítás',
            destructive: true,
        }))) return;
        try {
            await messageService.removeSent(messageId);
            setSent((prev) => prev.filter((m) => m.messageId !== messageId));
            if (selectedSent?.messageId === messageId) setSelectedSent(null);
            showSuccess('Az üzenet eltávolítva az elküldöttek közül.');
        } catch (err) {
            setSentError(apiErrorMessage(err, 'Az üzenetet nem sikerült eltávolítani. Próbáld újra.'));
        }
    };

    const handleMarkAllRead = async () => {
        try {
            await messageService.markAllRead();
            setMessages((prev) => prev.map((m) => ({ ...m, isRead: true })));
            onUnreadChange?.();
        } catch (err) {
            setError(apiErrorMessage(err, 'Nem sikerült olvasottnak jelölni az üzeneteket.'));
        }
    };

    const openCompose = (prefill?: ComposePrefill) => {
        setComposePrefill(prefill);
        setComposeOpen(true);
    };

    const handleReply = () => {
        // Rendszer-üzenetnél (nincs küldő) nincs kinek válaszolni — a gomb sem látszik.
        if (!selected?.senderId) return;
        openCompose({
            recipient: { userId: selected.senderId, name: selected.senderName },
            subject: selected.subject.startsWith('Re: ') ? selected.subject : `Re: ${selected.subject}`,
        });
    };

    const unreadCount = messages.filter((m) => !m.isRead).length;

    const renderList = () => {
        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            );
        }
        if (error) {
            return <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>;
        }
        if (messages.length === 0) {
            return (
                <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                    Nincs üzeneted.
                </Typography>
            );
        }
        return (
            <List disablePadding>
                {messages.map((m, idx) => (
                    <React.Fragment key={m.messageId}>
                        {idx > 0 && <Divider component="li" />}
                        <ListItemButton onClick={() => openMessage(m)} sx={{ alignItems: 'flex-start', py: 1.5 }}>
                            <Box sx={{ width: 18, pt: 0.75, flexShrink: 0 }}>
                                {!m.isRead && (
                                    <FiberManualRecordIcon color="primary" sx={{ fontSize: 10 }} />
                                )}
                            </Box>
                            <ListItemText
                                primary={m.subject}
                                primaryTypographyProps={{ fontWeight: m.isRead ? 400 : 700 }}
                                secondary={
                                    <>
                                        <Typography component="span" variant="body2" color="text.secondary" display="block" noWrap>
                                            {m.preview}
                                        </Typography>
                                        <Typography component="span" variant="caption" color="text.disabled">
                                            {m.senderName} · {formatDate(m.sentDate)}
                                        </Typography>
                                    </>
                                }
                            />
                            <Tooltip title="Üzenet törlése">
                                <IconButton
                                    edge="end"
                                    size="small"
                                    aria-label="Üzenet törlése"
                                    onClick={(e) => {
                                        // Különben a sor kattintása is elsülne, és megnyílna az üzenet.
                                        e.stopPropagation();
                                        handleDelete(m.messageId, m.subject);
                                    }}
                                >
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </ListItemButton>
                    </React.Fragment>
                ))}
            </List>
        );
    };

    const renderSentList = () => {
        if (sentLoading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            );
        }
        if (sentError) {
            return <Alert severity="error" sx={{ my: 2 }}>{sentError}</Alert>;
        }
        if (sent.length === 0) {
            return (
                <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                    Még nem küldtél üzenetet.
                </Typography>
            );
        }
        return (
            <List disablePadding data-testid="sent-list">
                {sent.map((m, idx) => (
                    <React.Fragment key={m.messageId}>
                        {idx > 0 && <Divider component="li" />}
                        <ListItemButton onClick={() => openSent(m)} sx={{ alignItems: 'flex-start', py: 1.5 }}>
                            <ListItemText
                                primary={m.subject}
                                secondary={
                                    <>
                                        <Typography component="span" variant="body2" color="text.secondary" display="block" noWrap>
                                            {m.preview}
                                        </Typography>
                                        <Typography component="span" variant="caption" color="text.disabled">
                                            {formatDate(m.sentDate)} · {m.recipientCount} címzett · {m.readCount} olvasta
                                        </Typography>
                                    </>
                                }
                            />
                            <Tooltip title="Eltávolítás az elküldöttek közül">
                                <IconButton
                                    edge="end"
                                    size="small"
                                    aria-label="Eltávolítás az elküldöttek közül"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteSent(m.messageId, m.subject);
                                    }}
                                >
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </ListItemButton>
                    </React.Fragment>
                ))}
            </List>
        );
    };

    const renderDetail = () => {
        if (detailLoading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            );
        }
        if (detailDeleted) {
            return (
                <Alert severity="info" sx={{ my: 2 }}>
                    Ezt az üzenetet törölted. Az üzenet már nem szerepel a postafiókodban.
                </Alert>
            );
        }
        if (detailError) {
            return <Alert severity="error" sx={{ my: 2 }}>{detailError}</Alert>;
        }
        if (selectedSent) {
            return (
                <Box>
                    <Typography variant="h6" sx={{ mb: 0.5 }}>{selectedSent.subject}</Typography>
                    <Typography variant="caption" color="text.disabled">
                        {formatDate(selectedSent.sentDate)} · {selectedSent.recipientCount} címzett · {selectedSent.readCount} olvasta
                    </Typography>
                    <Typography sx={{ mt: 2, whiteSpace: 'pre-wrap' }}>{selectedSent.body}</Typography>
                    <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {selectedSent.recipients.map((r) => (
                            <Chip
                                key={r.userId}
                                size="small"
                                label={r.name}
                                color={r.isRead ? 'success' : 'default'}
                                variant={r.isRead ? 'filled' : 'outlined'}
                                title={r.isRead ? `Olvasta: ${formatDate(r.readDate ?? '')}` : 'Még nem olvasta'}
                            />
                        ))}
                    </Box>
                </Box>
            );
        }
        if (!selected) return null;
        return (
            <Box>
                <Typography variant="h6" sx={{ mb: 0.5 }}>{selected.subject}</Typography>
                <Typography variant="caption" color="text.disabled">
                    {selected.senderName} · {formatDate(selected.sentDate)}
                </Typography>
                {/* A teljes szöveg, sortörésekkel ahogy a küldő megírta */}
                <Typography sx={{ mt: 2, whiteSpace: 'pre-wrap' }}>{selected.body}</Typography>
                {selected.eventId && (
                    <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                        <EventIcon fontSize="small" />
                        <Typography variant="body2">
                            Kapcsolódó esemény: {selected.eventName || 'ismeretlen'}
                        </Typography>
                    </Box>
                )}
            </Box>
        );
    };

    const showingDetail = Boolean(selected) || Boolean(selectedSent) || detailLoading || detailDeleted || Boolean(detailError);

    const backToList = () => {
        setSelected(null);
        setSelectedSent(null);
        setDetailError('');
        setDetailDeleted(false);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 2 }}>
                {showingDetail && (
                    <IconButton aria-label="Vissza a postafiókhoz" onClick={backToList} size="small">
                        <ArrowBackIcon />
                    </IconButton>
                )}
                <Box sx={{ flexGrow: 1 }}>{showingDetail ? 'Üzenet' : 'Üzenetek'}</Box>
                {!showingDetail && view === 'inbox' && unreadCount > 0 && (
                    <Button size="small" onClick={handleMarkAllRead}>Mind olvasott</Button>
                )}
                {!showingDetail && (
                    <Button size="small" variant="contained" startIcon={<CreateIcon />} onClick={() => openCompose()}>
                        Új üzenet
                    </Button>
                )}
            </DialogTitle>
            {!showingDetail && (
                <Tabs value={view} onChange={(_, v: InboxView) => setView(v)} sx={{ px: 2 }}>
                    <Tab value="inbox" label="Beérkezett" />
                    <Tab value="sent" label="Elküldött" />
                </Tabs>
            )}
            <DialogContent dividers>
                {showingDetail ? renderDetail() : view === 'inbox' ? renderList() : renderSentList()}
            </DialogContent>
            <DialogActions>
                {showingDetail && selected && (
                    <>
                        <Button
                            color="error"
                            startIcon={<DeleteOutlineIcon />}
                            onClick={() => handleDelete(selected.messageId, selected.subject)}
                            sx={{ mr: 'auto' }}
                        >
                            Törlés
                        </Button>
                        {/* Válasz csak akkor, ha van kinek: rendszer-üzenetnek nincs küldője */}
                        {selected.senderId && (
                            <Button startIcon={<ReplyIcon />} onClick={handleReply}>
                                Válasz
                            </Button>
                        )}
                    </>
                )}
                {showingDetail && selectedSent && (
                    <Button
                        color="error"
                        startIcon={<DeleteOutlineIcon />}
                        onClick={() => handleDeleteSent(selectedSent.messageId, selectedSent.subject)}
                        sx={{ mr: 'auto' }}
                    >
                        Eltávolítás
                    </Button>
                )}
                <Button onClick={onClose}>Bezárás</Button>
            </DialogActions>

            <ComposeMessageDialog
                open={composeOpen}
                onClose={() => setComposeOpen(false)}
                onSent={() => { if (view === 'sent') loadSent(); }}
                prefill={composePrefill}
            />
        </Dialog>
    );
};

export default MessageInbox;
